/**
 * Busy state of editors that live OUTSIDE App.tsx's own state.
 *
 * App.tsx decides whether a new deployment may reload the tab
 * (`decideOnVeraltet`). It only saw the project report editor (`report`,
 * `saveStatus`); an open quote editor counted as idle, and
 * `window.location.reload()` threw away unsaved quote edits. The quote editor
 * lives deep inside the CRM view, so it reports here instead of threading a
 * callback through App.
 *
 * Plain module state plus a subscription, so App can read it synchronously in
 * a callback AND re-render (useSyncExternalStore) when the editor closes —
 * that is when a deferred reload may run.
 */

export interface QuoteEditorBusy {
    /** A quote is open in the editor. */
    open: boolean;
    /** A save of that quote is in flight. */
    saving: boolean;
}

const IDLE: QuoteEditorBusy = { open: false, saving: false };

// Replaced (never mutated) on change: the snapshot must stay referentially
// stable between changes for useSyncExternalStore.
let quoteBusy: QuoteEditorBusy = IDLE;
const listeners = new Set<() => void>();

/**
 * Report the quote editor's state. Call with `(true, saving)` while a quote
 * is open and `(false, false)` when it closes/unmounts.
 */
export const setQuoteEditorBusy = (open: boolean, saving: boolean = false): void => {
    // Saving without an open editor still blocks a reload: the request is in
    // flight and a reload would abort it.
    if (quoteBusy.open === open && quoteBusy.saving === saving) return;
    quoteBusy = !open && !saving ? IDLE : { open, saving };
    listeners.forEach((l) => l());
};

/** Current quote editor state (stable reference until the next change). */
export const getQuoteEditorBusy = (): QuoteEditorBusy => quoteBusy;

/** Subscribe to changes; returns the unsubscribe function. */
export const subscribeEditorBusy = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

/**
 * Combines App's own report editor state with the registered quote editor.
 * Pure — the reload decision takes these two booleans.
 */
export const combineEditorBusy = (
    report: { editorOpen: boolean; saving: boolean },
    quote: QuoteEditorBusy,
): { editorOpen: boolean; saving: boolean } => ({
    editorOpen: report.editorOpen || quote.open,
    saving: report.saving || quote.saving,
});
